/**
 * OfflineIndicator.tsx — Sticky banner shown when the browser loses connectivity.
 *
 * Usage: mount once in App.tsx (or Layout) near the top of the component tree.
 * The banner slides in from the top and disappears automatically when back online.
 *
 * Also exposes a `useIsOnline()` hook for other components that need to react to
 * connectivity state.
 */

import { useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useIsOnline(): boolean {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const setOnline  = () => setIsOnline(true);
    const setOffline = () => setIsOnline(false);

    window.addEventListener('online',  setOnline);
    window.addEventListener('offline', setOffline);

    return () => {
      window.removeEventListener('online',  setOnline);
      window.removeEventListener('offline', setOffline);
    };
  }, []);

  return isOnline;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OfflineIndicator() {
  const isOnline = useIsOnline();
  const [wasOffline, setWasOffline] = useState(false);
  const [showBackOnline, setShowBackOnline] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    } else if (wasOffline) {
      // Just came back online — show confirmation for 3 s
      setShowBackOnline(true);
      const timer = setTimeout(() => setShowBackOnline(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  if (isOnline && !showBackOnline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-all duration-300 ${
        !isOnline
          ? 'bg-red-600 text-white'
          : 'bg-green-600 text-white'
      }`}
    >
      {!isOnline ? (
        <>
          <span className="inline-block w-2 h-2 rounded-full bg-white opacity-80 animate-pulse" />
          Sin conexión — Modo offline activo. Las ventas se guardan localmente.
        </>
      ) : (
        <>
          <span>✓</span>
          Conexión restaurada
        </>
      )}
    </div>
  );
}
